import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit2, Trash2, Key, Shield, 
  UserCheck, UserX, X, Save, ChevronLeft
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const navigate = useNavigate();
  const { user: currentUser, getAuthHeader, isAdmin, loading: authLoading } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'editor'
  });

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (authLoading) return;
    
    // Allow both admin and editor roles to manage users
    fetchUsers();
  }, [authLoading]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingUser 
        ? `${BACKEND_URL}/api/admin/users/${editingUser.id}`
        : `${BACKEND_URL}/api/admin/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      const body = editingUser 
        ? { first_name: formData.first_name, last_name: formData.last_name, role: formData.role }
        : formData;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(editingUser ? 'User updated!' : 'User created!');
        setShowModal(false);
        setEditingUser(null);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        toast.success('User deleted');
        fetchUsers();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        toast.success(currentStatus ? 'User deactivated' : 'User activated');
        fetchUsers();
      } else {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setTempPassword(data.temporary_password);
        setResetPasswordModal(userId);
      } else {
        throw new Error('Failed to reset password');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'editor'
    });
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role
    });
    setShowModal(true);
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-500/20 text-red-400 border-red-500/30',
      editor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      member: 'bg-green-500/20 text-green-400 border-green-500/30',
      customer: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      viewer: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[role] || colors.viewer;
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-3xl font-bold font-display text-foreground flex items-center gap-3">
                <Users className="w-8 h-8 text-primary" />
                User Management
              </h1>
              <p className="text-muted-foreground mt-1">Manage admin and editor accounts</p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setEditingUser(null); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105"
            data-testid="add-user-btn"
          >
            <Plus className="w-5 h-5" />
            Add User
          </button>
        </div>

        {/* Users Table */}
        <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b border-border/50 bg-background/50">
                <th className="text-left py-4 px-6 text-sm font-bold text-muted-foreground">User</th>
                <th className="text-left py-4 px-6 text-sm font-bold text-muted-foreground">Email</th>
                <th className="text-left py-4 px-6 text-sm font-bold text-muted-foreground">Role</th>
                <th className="text-left py-4 px-6 text-sm font-bold text-muted-foreground">Status</th>
                <th className="text-right py-4 px-6 text-sm font-bold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-b border-border/30 hover:bg-white/5 transition-colors"
                  data-testid={`user-row-${user.id}`}
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-black font-bold">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{user.first_name} {user.last_name}</p>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-primary">(You)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-muted-foreground">{user.email}</td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getRoleBadge(user.role)}`}>
                      {user.role?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    {user.is_active ? (
                      <span className="flex items-center gap-1 text-green-400 text-sm">
                        <UserCheck className="w-4 h-4" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-400 text-sm">
                        <UserX className="w-4 h-4" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                        title="Edit"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(user.id)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-primary"
                        title="Reset Password"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {user.id !== currentUser?.id && (
                        <>
                          <button
                            onClick={() => handleToggleActive(user.id, user.is_active)}
                            className={`p-2 hover:bg-white/10 rounded-lg transition-colors ${user.is_active ? 'text-orange-400' : 'text-green-400'}`}
                            title={user.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-red-400 hover:text-red-300"
                            title="Delete"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          
          {users.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          )}
        </div>

        {/* Roles Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-red-400" />
              <h3 className="font-bold text-foreground">Admin</h3>
            </div>
            <p className="text-sm text-muted-foreground">Full access. Can manage users, settings, and all content.</p>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Edit2 className="w-5 h-5 text-blue-400" />
              <h3 className="font-bold text-foreground">Editor</h3>
            </div>
            <p className="text-sm text-muted-foreground">Can manage music, videos, classes, and view orders.</p>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-gray-400" />
              <h3 className="font-bold text-foreground">Viewer</h3>
            </div>
            <p className="text-sm text-muted-foreground">Read-only access to dashboard and reports.</p>
          </div>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/50 rounded-2xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditingUser(null); }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="user-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="user-last-name"
                  />
                </div>
              </div>

              {!editingUser && (
                <>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="user-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-2">Password</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Minimum 6 characters"
                      data-testid="user-password"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="user-role"
                >
                  <option value="admin">Administrator</option>
                  <option value="editor">Editor</option>
                  <option value="member">Member</option>
                  <option value="customer">Customer</option>
                  <option value="viewer">Viewer</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Admin: Full access | Editor: Content management | Member: Paying member | Customer: One-time buyer | Viewer: Read-only
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingUser(null); }}
                  className="flex-1 px-4 py-3 border border-border rounded-xl text-foreground hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold transition-colors"
                  data-testid="save-user-btn"
                >
                  <Save className="w-4 h-4" />
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/50 rounded-2xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">Password Reset</h2>
              <button
                onClick={() => { setResetPasswordModal(null); setTempPassword(''); }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mb-4">
              <p className="text-sm text-muted-foreground mb-2">Temporary password:</p>
              <p className="font-mono text-lg text-primary font-bold" data-testid="temp-password">{tempPassword}</p>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Share this temporary password with the user. They should change it after logging in.
            </p>

            <button
              onClick={() => {
                navigator.clipboard.writeText(tempPassword);
                toast.success('Password copied to clipboard');
              }}
              className="w-full px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold transition-colors"
            >
              Copy to Clipboard
            </button>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
